import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const NeuroElectroPhysiologyCoursePage: React.FC = () => {
  const neuroElectroData = {
    title: "Bachelor of Science in Neuro Electro Physiology Technology",
    shortName: "B.Sc NEPT",
    duration: "3 Years",
    description: "A specialized program focusing on neurophysiological testing, brain and nerve function assessment using advanced electrophysiological equipment and techniques.",
    overview: "B.Sc Neuro Electro Physiology Technology prepares students to become skilled neurophysiology technologists who conduct various neurological tests including EEG, EMG, NCV, and evoked potentials. The program covers neuroanatomy, neurophysiology, equipment operation, test procedures, and data interpretation. Students learn to assist neurologists in diagnosing neurological disorders through sophisticated electrophysiological testing.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in neuroscience and technology",
      "Good concentration and attention to detail",
      "Manual dexterity for equipment operation"
    ],
    careerProspects: {
      roles: [
        "EEG Technologist",
        "EMG Technologist",
        "Neurophysiology Technologist",
        "Polysomnography Technician",
        "Evoked Potential Technologist",
        "Nerve Conduction Specialist",
        "Sleep Study Technician",
        "Neurological Equipment Specialist",
        "Research Technician",
        "Clinical Coordinator"
      ],
      industries: [
        "Neurology Hospitals",
        "Sleep Disorder Centers",
        "Diagnostic Centers",
        "Neurosurgical Units",
        "Rehabilitation Centers",
        "Research Institutions",
        "Medical Equipment Companies",
        "Government Hospitals",
        "Private Clinics",
        "Academic Medical Centers"
      ],
      averageSalary: "₹3.5-8 LPA (varies by experience and specialization)",
      topRecruiters: [
        "NIMHANS",
        "AIIMS Neurology",
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Medanta",
        "Max Healthcare",
        "Manipal Hospitals",
        "Neurological Equipment Companies",
        "Research Institutions",
        "Government Medical Colleges"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Neuroanatomy",
          "Basic Physiology",
          "Electronics Fundamentals",
          "Medical Terminology",
          "Computer Applications",
          "Communication Skills",
          "Introduction to Neurophysiology"
        ]
      },
      {
        year: 2,
        subjects: [
          "Neurophysiology",
          "EEG Technology",
          "EMG and NCV Studies",
          "Evoked Potentials",
          "Sleep Studies",
          "Equipment Maintenance",
          "Patient Care",
          "Data Analysis"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Neurophysiology",
          "Polysomnography",
          "Intraoperative Monitoring",
          "Pediatric Neurophysiology",
          "Research Methods",
          "Quality Control",
          "Clinical Internship",
          "Professional Ethics"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc NEPT",
      "Entrance examination (typically NEET-based)",
      "Merit-based selection",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Orientation program",
      "Begin academic and clinical training"
    ],
    topColleges: [
      {
        name: "National Institute of Mental Health and Neurosciences",
        location: "Bangalore, Karnataka",
        fees: "₹25,000 PA",
        ranking: "Premier Neuroscience Institute"
      },
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹5,000 PA",
        ranking: "Top Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Government Medical College",
        location: "Chandigarh",
        fees: "₹30,000 PA",
        ranking: "Government Medical College"
      }
    ],
    skills: [
      "Equipment Operation",
      "Technical Proficiency",
      "Attention to Detail",
      "Patient Interaction",
      "Data Interpretation",
      "Problem Solving",
      "Manual Dexterity",
      "Computer Skills",
      "Documentation",
      "Quality Assurance",
      "Safety Protocols",
      "Continuous Learning"
    ],
    futureScope: "The field is expanding with advanced neurological monitoring, brain-computer interfaces, and artificial intelligence in neurophysiology. Future opportunities include specialization in pediatric neurophysiology, intraoperative monitoring, advanced sleep studies, and emerging technologies like transcranial magnetic stimulation and deep brain stimulation monitoring.",
    faqs: [
      {
        question: "What is the difference between EEG and EMG?",
        answer: "EEG (Electroencephalography) measures brain electrical activity, while EMG (Electromyography) measures electrical activity in muscles. Both are important diagnostic tools in neurophysiology."
      },
      {
        question: "Is this field suitable for students without strong science background?",
        answer: "A strong foundation in biology and physics is important as the field involves understanding neurophysiology and operating sophisticated electronic equipment."
      },
      {
        question: "Can I work in research after this degree?",
        answer: "Yes, many graduates work in neuroscience research, particularly in areas like epilepsy research, sleep disorders, and neurological disease studies."
      },
      {
        question: "What are sleep studies and their career prospects?",
        answer: "Sleep studies (polysomnography) diagnose sleep disorders. With increasing awareness of sleep health, this specialization offers excellent career growth in sleep centers and hospitals."
      },
      {
        question: "Is certification required for practice?",
        answer: "While not mandatory in India, professional certifications from organizations like ASET (American Society of Electroneurodiagnostic Technologists) enhance career prospects and credibility."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {neuroElectroData.title} ({neuroElectroData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={neuroElectroData} />
      </div>
    </div>
  );
};

export default NeuroElectroPhysiologyCoursePage;